import axiosClient, { publicAxiosClient } from "./axios";
import { openSnackbar } from 'api/snackbar';
import dayjs from 'dayjs';
import { BASE_URL } from 'utils/baseUrlProvider';
import { getDisplayQuantity } from "./prescriptionHelper";
import { resolvePricingValue } from "./insurancePricing";

// api helper for prescription creation

export const checkInternetConnection = async () => {
    try {
        const response = await axiosClient.get(`${BASE_URL}/miscellaneous/get_internet_status/`);
        return response.data.internet === true;
    } catch (error) {
        console.error('Error checking internet status:', error);
        throw error;
    }
};

export const sendDeepLinkSMS = async (patientId) => {
    try {
        const response = await axiosClient.post(`${BASE_URL}/abha/api/v3/hip-link/send_deep_link_sms/`, {
            patient_id: patientId
        });
        return response.data;
    } catch (error) {
        console.error('Error sending ABHA SMS:', error);
        throw error;
    }
};

export const checkSmsStatus = async (requestId) => {
    try {
        const response = await axiosClient.get(`${BASE_URL}/abha/api/v3/hip-link/check_sms_status/?requestId=${requestId}`);
        return response.data;
    } catch (error) {
        console.error('Error checking SMS status:', error);
        throw error;
    }
};

export const fetchAppointments = async (doctorId, date, searchQuery, nextQueryURL) => {
    let url = `${BASE_URL}/opd/appointments/?doctor_id=${doctorId}`;
    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        url += `&appointment_date=${formattedDate}`;
    }
    if (searchQuery) {
        url += `&q=${searchQuery}&${nextQueryURL}`;
    } else if (nextQueryURL) {
        url += `&${nextQueryURL}`;
    }
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchLastAppointment = async (patientId, forDoctor = true) => {
    const url = `opd/appointments/last-appointment/?patient_id=${patientId}&for_doctor=${forDoctor}`;
    try {
        const response = await axiosClient.get(url, { skipErrorSnackbarStatuses: [404] });
        const data = response.data;
        return Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
    } catch (error) {
        if (error.response?.status === 404) return [];
        throw error;
    }
}

/** All completed OPD visits for a patient (every doctor) – for copy-prescription "All doctors". */
export const fetchPatientCompletedAppointments = async (patientId, limit = 100) => {
    const url = `${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED&ordering=-created_at&limit=${limit}`;
    const response = await axiosClient.get(url);
    const data = response.data;
    return Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
}

export const fetchPatientVisitHistory = async (patientId, limit = 5) => {
    const url = `${BASE_URL}/opd/appointments/?patient_id=${patientId}&ordering=-created_at&limit=${limit}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching patient visit history:', error);
        throw error;
    }
};

export const fetchAllAppointments = async (date, searchQuery, nextQueryURL) => {
    let url = `${BASE_URL}/opd/appointments/?`;
    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        url += `appointment_date=${formattedDate}`;
    }
    if (searchQuery) {
        url += `&q=${searchQuery}&${nextQueryURL}`;
    } else if (nextQueryURL) {
        url += `&${nextQueryURL}`;
    }
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchReceptionAppointments = async (date, searchQuery, nextQueryURL, doctorId) => {
    let url = `${BASE_URL}/opd/appointments/?ordering=-updated_at`;
    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        url += `&appointment_date=${formattedDate}`;
    }
    if (searchQuery) {
        url += `&q=${searchQuery}`;
    }
    if (doctorId) {
        url += `&doctor_id=${doctorId}`;
    }
    if (nextQueryURL) {
        url += `&${nextQueryURL}`;
    }
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};


export const fetchPatientAppointment = async (id) => {
    const url = `${BASE_URL}/opd/appointments/${id}/`;
    try {
        let response = await axiosClient.get(url);
        response = {
            ...response,
            data: {
                ...response.data,
                vital_details: {
                    ...response.data.vital_details,
                    blood_pressure: response.data.vital_details?.blood_pressure !== '/' && response.data.vital_details?.blood_pressure !== '' ? response.data.vital_details?.blood_pressure : '',
                }
            }
        };
        return response.data;
    } catch (error) {
        throw error;
    }
};

export {
  fetchPatientSummary,
  fetchIPDDischargeSummary,
  fetchPatientSummaryWithReveal,
  fetchIPDDischargeSummaryWithReveal
} from "api/aiSummary";

export const fetchAppointmentStats = async () => {
    const url = `${BASE_URL}/opd/doctor/preferences/`;
    try {
        const response = await axiosClient.get(url);

        // Validate response - ensure it's not HTML
        const responseData = response.data;
        const isHTML =
            typeof responseData === 'string' &&
            (() => {
                const responseText = responseData.toLowerCase();
                return (
                    responseText.includes('<!doctype') ||
                    responseText.includes('<html')
                );
            })();

        if (isHTML) {
            throw new Error('API returned HTML instead of JSON - possible error page');
        }

        return responseData;
    } catch (error) {
        throw error;
    }
};

export const fetchSmartSuggestions = async (contextItems, signal) => {
    const url = `${BASE_URL}/opd/doctor/smart-suggestions/`;
    const params = new URLSearchParams();
    contextItems.forEach((item) => params.append('context', item));
    const response = await axiosClient.get(`${url}?${params.toString()}`, { signal });
    return response.data;
};

export const warmSmartSuggestions = async () => {
    const url = `${BASE_URL}/opd/doctor/smart-suggestions/warm/`;
    const response = await axiosClient.post(url);
    return response.data;
};



export const fetchStats = async (id, date) => {
    let url = `${BASE_URL}/opd/appointments/stats?doctor_id=${id}`;
    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        url += `&date=${formattedDate}`;
    }
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchAllStats = async (date) => {
    let url = `${BASE_URL}/opd/appointments/stats`;
    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        url += `?date=${formattedDate}`;
    }
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Fetch IPD bed summary
export const fetchBedSummary = async () => {
    const url = `${BASE_URL}/ipd/beds/summary/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching IPD bed summary:', error);
        throw error;
    }
};

// Fetch IPD admissions summary
export const fetchAdmissionsSummary = async (startDate, endDate) => {
    const url = `${BASE_URL}/ipd/admissions/summary/`;
    try {
        const response = await axiosClient.get(url, {
            params: {
                ...(startDate && { start_date: startDate }),
                ...(endDate && { end_date: endDate })
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching IPD admissions summary:', error);
        throw error;
    }
};

// List beds with optional query params (pagination, filters)
export const fetchBeds = async (params) => {
    let url = `${BASE_URL}/ipd/beds/`;
    try {
        if (params && typeof params === 'object') {
            const qs = new URLSearchParams(params).toString();
            if (qs) url += `?${qs}`;
        }
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching beds:', error);
        throw error;
    }
};

// Create a new bed
export const createBed = async (data) => {
    const url = `${BASE_URL}/ipd/beds/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        console.error('Error creating bed:', error);
        throw error;
    }
};

// Retrieve a single bed by id
export const fetchBed = async (id) => {
    const url = `${BASE_URL}/ipd/beds/${id}/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error(`Error fetching bed ${id}:`, error);
        throw error;
    }
};

// Full update a bed (PUT)
export const updateBed = async (id, data) => {
    const url = `${BASE_URL}/ipd/beds/${id}/`;
    try {
        const response = await axiosClient.put(url, data);
        return response.data;
    } catch (error) {
        console.error(`Error updating bed ${id}:`, error);
        throw error;
    }
};

// Partial update a bed (PATCH)
export const patchBed = async (id, data) => {
    const url = `${BASE_URL}/ipd/beds/${id}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        console.error(`Error patching bed ${id}:`, error);
        throw error;
    }
};

// Delete a bed
export const deleteBed = async (id) => {
    const url = `${BASE_URL}/ipd/beds/${id}/`;
    try {
        const response = await axiosClient.delete(url);
        return response.data;
    } catch (error) {
        console.error(`Error deleting bed ${id}:`, error);
        throw error;
    }
};

export const updateBedStatus = async (id, statusPayload) => {
    const url = `${BASE_URL}/ipd/beds/${id}/status/`;
    try {
        const response = await axiosClient.patch(url, statusPayload);
        return response.data;
    } catch (error) {
        console.error(`Error updating status for bed ${id}:`, error);
        throw error;
    }
};

export const fetchAdmissions = async (params) => {
    let url = `${BASE_URL}/ipd/admissions/`;
    try {
        if (params && typeof params === 'object') {
            const qs = new URLSearchParams(params).toString();
            if (qs) url += `?${qs}`;
        }
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchAdmission = async (id) => {
    const url = `${BASE_URL}/ipd/admissions/${id}/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createAdmission = async (data) => {
    const url = `${BASE_URL}/ipd/admissions/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const cancelAdmission = async (id) => {
    const url = `${BASE_URL}/ipd/admissions/${id}/`;
    try {
        const response = await axiosClient.delete(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchDischargeRecords = async (params) => {
    let url = `${BASE_URL}/ipd/discharge-records/`;
    try {
        if (params && typeof params === 'object') {
            const qs = new URLSearchParams(params).toString();
            if (qs) url += `?${qs}`;
        }
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createDischarge = async (data) => {
    const url = `${BASE_URL}/ipd/discharge-records/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchBedTransfers = async (params) => {
    let url = `${BASE_URL}/ipd/bed-transfers/`;
    try {
        if (params && typeof params === 'object') {
            const qs = new URLSearchParams(params).toString();
            if (qs) url += `?${qs}`;
        }
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchBedTransferById = async (id) => {
    const url = `${BASE_URL}/ipd/bed-transfers/${id}/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createBedTransfer = async (data) => {
    const url = `${BASE_URL}/ipd/bed-transfers/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Reservations API helpers
export const fetchReservations = async (params) => {
    let url = `${BASE_URL}/ipd/reservations/`;
    try {
        if (params && typeof params === 'object') {
            const qs = new URLSearchParams(params).toString();
            if (qs) url += `?${qs}`;
        }
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching reservations:', error);
        throw error;
    }
};

// Retrieve a single reservation by id
export const fetchReservation = async (id) => {
    const url = `${BASE_URL}/ipd/reservations/${id}/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error(`Error fetching reservation ${id}:`, error);
        throw error;
    }
};

export const createReservation = async (data) => {
    const url = `${BASE_URL}/ipd/reservations/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        console.error('Error creating reservation:', error);
        throw error;
    }
};

export const updateReservation = async (id, data) => {
    const url = `${BASE_URL}/ipd/reservations/${id}/`;
    try {
        const response = await axiosClient.put(url, data);
        return response.data;
    } catch (error) {
        console.error(`Error updating reservation ${id}:`, error);
        throw error;
    }
};

export const patchReservation = async (id, data) => {
    const url = `${BASE_URL}/ipd/reservations/${id}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        console.error(`Error patching reservation ${id}:`, error);
        throw error;
    }
};

export const fetchSymptoms = async (searchQuery) => {
    const url = `${BASE_URL}/opd/search-sym?q=${searchQuery}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchDiagnosis = async (searchQuery) => {
    const url = `${BASE_URL}/opd/search-dd?q=${searchQuery}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};



export const fetchLabTests = async (searchQuery, isActive = true) => {
    const url = `${BASE_URL}/lab/lab-tests/?search=${searchQuery}&ordering=-updated_at${isActive ? '&is_active=true' : ''}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching lab tests:", error);
        throw error;
    }
};

export const fetchLabTestsAndPackages = async (searchQuery, isActive = true, patientId = null, billingProvider = null) => {
    try {
        const isActiveParam = isActive ? '&is_active=true' : '';
        const ignoreGroupParam = '&ignore_group_ids=true';
        const patientParam = patientId ? `&patient_id=${patientId}` : '';
        const billingProviderParam = billingProvider ? `&billing_provider=${billingProvider}` : '';
        const [testsResponse, packagesResponse] = await Promise.all([
            axiosClient.get(`${BASE_URL}/lab/lab-tests/?search=${searchQuery}&ordering=-updated_at${isActiveParam}${ignoreGroupParam}${patientParam}${billingProviderParam}`),
            axiosClient.get(`${BASE_URL}/lab/lab-packages/?search=${searchQuery}&ordering=-updated_at${isActiveParam}${ignoreGroupParam}${patientParam}${billingProviderParam}`)
        ]);

        const tests = testsResponse.data.results || [];
        const packages = packagesResponse.data.results || [];

        // Filter out inactive items (additional safety check)
        // Only include items where is_active is explicitly true
        const activeTests = tests.filter(test => test.is_active === true);
        // For packages, is_active is nested inside master_test
        const activePackages = packages.filter(pkg => pkg.master_test?.is_active === true);

        const formattedPackages = activePackages.map(pkg => ({
            id: pkg.master_test?.id || pkg.id,
            name: `📦 ${pkg.master_test?.name || pkg.name || 'Lab Package'}`,
            price: resolvePricingValue(
                pkg.master_test?.pricing,
                pkg.master_test?.hospital_code
            ) ?? pkg.price ?? 0,
            pricing: pkg.master_test?.pricing,
            hospital_code: pkg.master_test?.hospital_code,
            type: 'package',
            originalData: pkg
        }));

        const formattedTests = activeTests.map(test => ({
            id: test.id,
            name: test.name,
            price: resolvePricingValue(test.pricing, test.hospital_code) ?? 0,
            pricing: test.pricing,
            hospital_code: test.hospital_code,
            type: 'test',
            originalData: test
        }));

        const combined = [...formattedPackages, ...formattedTests];
        return {
            results: combined,
            count: combined.length
        };
    } catch (error) {
        console.error("Error fetching lab tests and packages:", error);
        throw error;
    }
};

// Floors API
export const fetchFloors = async (params) => {
    let url = `${BASE_URL}/ipd/floors/`;
    try {
        if (params && typeof params === 'object') {
            const qs = new URLSearchParams(params).toString();
            if (qs) url += `?${qs}`;
        }
        const response = await axiosClient.get(url);
        return response.data; // expected shape: { floors: [...], meta?: {...} }
    } catch (error) {
        console.error('Error fetching floors:', error);
        throw error;
    }
};

export const createFloor = async (data) => {
    const url = `${BASE_URL}/ipd/floors/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        console.error('Error creating floor:', error);
        throw error;
    }
};

// Update a floor
export const updateFloor = async (id, data) => {
    const url = `${BASE_URL}/ipd/floors/${id}/`;
    try {
        const response = await axiosClient.put(url, data);
        return response.data;
    } catch (error) {
        console.error(`Error updating floor ${id}:`, error);
        throw error;
    }
};

// Retrieve a single floor by id
export const fetchFloor = async (id) => {
    const url = `${BASE_URL}/ipd/floors/${id}/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error(`Error fetching floor ${id}:`, error);
        throw error;
    }
};

// Delete a floor by id
export const deleteFloor = async (id) => {
    const url = `${BASE_URL}/ipd/floors/${id}/`;
    try {
        const response = await axiosClient.delete(url);
        return response.data;
    } catch (error) {
        console.error(`Error deleting floor ${id}:`, error);
        throw error;
    }
};

// Rooms API
export const fetchRooms = async (params) => {
    let url = `${BASE_URL}/ipd/rooms/`;
    try {
        if (params && typeof params === 'object') {
            const qs = new URLSearchParams(params).toString();
            if (qs) url += `?${qs}`;
        }
        const response = await axiosClient.get(url);
        return response.data; // expected shape: { rooms: [...], meta?: {...} }
    } catch (error) {
        console.error('Error fetching rooms:', error);
        throw error;
    }
};

export const createRoom = async (data) => {
    const url = `${BASE_URL}/ipd/rooms/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        console.error('Error creating room:', error);
        throw error;
    }
};

export const updateRoom = async (id, data) => {
    const url = `${BASE_URL}/ipd/rooms/${id}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        console.error(`Error updating room ${id}:`, error);
        throw error;
    }
};

// Retrieve a single room by id
export const fetchRoom = async (id) => {
    const url = `${BASE_URL}/ipd/rooms/${id}/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error(`Error fetching room ${id}:`, error);
        throw error;
    }
};

// Delete a room
export const deleteRoom = async (id) => {
    const url = `${BASE_URL}/ipd/rooms/${id}/`;
    try {
        const response = await axiosClient.delete(url);
        return response.data;
    } catch (error) {
        console.error(`Error deleting room ${id}:`, error);
        throw error;
    }
};

export const fetchMedicine = async (searchQuery, isActive = true) => {
    const params = new URLSearchParams();
    if (searchQuery) params.append('q', searchQuery);
    if (isActive) params.append('is_active', 'true');
    const url = `${BASE_URL}/pharmacy/medicines/search/?${params.toString()}`;
    try {
        const response = await axiosClient.get(url);
        const data = response.data;
        if (data && !Array.isArray(data) && Array.isArray(data.results)) {
            return data.results;
        }
        return data;
    } catch (error) {
        throw error;
    }
}

export const addMedicine = async (data) => {
    const url = `${BASE_URL}/pharmacy/medicines/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const updateMedicine = async (id, data) => {
    const url = `${BASE_URL}/pharmacy/medicines/${id}/`; // Need to change the URL
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}


// for prescription submit
export const prescriptionSubmit = async (id, data) => {
    const url = `${BASE_URL}/opd/appointments/${id}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchDoctors = async () => {
    const url = `${BASE_URL}/users/doctors/`;
    try {
        const response = await axiosClient.get(url);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        throw error;
    }
};

export const fetchPublicDoctors = async (params = {}) => {
    const response = await publicAxiosClient.get(`${BASE_URL}/users/public/doctors/`, { params });
    return response.data;
};

export const fetchPublicDoctorSlots = async (doctorId) => {
    const response = await publicAxiosClient.get(
        `${BASE_URL}/users/public/doctors/${encodeURIComponent(doctorId)}/slots/`
    );
    return response.data;
};

export const requestPatientBookingOTP = async (phoneNumber) => {
    const response = await publicAxiosClient.post(
        `${BASE_URL}/patient/request-otp/`,
        { phone_number: phoneNumber, online_booking: true }
    );
    return response.data;
};

export const verifyPublicBookingOTP = async (otpData) => {
    const response = await publicAxiosClient.post(
        `${BASE_URL}/opd/public/booking-otp/verify/`,
        otpData
    );
    return response.data;
};

export const addPublicBookingPatient = async (patientData) => {
    const response = await publicAxiosClient.post(
        `${BASE_URL}/opd/public/booking-patients/`,
        patientData
    );
    return response.data;
};

// Fee for the selected patient (new / old / follow-up) plus the platform fee on it.
export const fetchPublicBookingQuote = async (quoteData) => {
    const response = await publicAxiosClient.post(
        `${BASE_URL}/opd/public/booking-quote/`,
        quoteData
    );
    return response.data;
};

// Staff-side fee for a doctor and (optionally) patient: { fee_type, fee, standard_fee }.
export const fetchConsultationFee = async (doctorId, patientId) => {
    const response = await axiosClient.get(`${BASE_URL}/opd/consultation-fee/`, {
        params: { doctor_id: doctorId, ...(patientId ? { patient_id: patientId } : {}) }
    });
    return response.data;
};

// Server clock minus device clock, so hold countdowns don't depend on the device clock.
// Uses the intent's server_time, falling back to the Date header (0 if neither is readable).
const serverClockOffset = (response) => {
    const serverTime = Date.parse(response.data?.server_time || response.headers?.date || '');
    return Number.isNaN(serverTime) ? 0 : serverTime - Date.now();
};

export const createPublicBookingIntent = async (bookingData) => {
    const response = await publicAxiosClient.post(
        `${BASE_URL}/opd/public/booking-intents/`,
        bookingData
    );
    return { ...response.data, clock_offset_ms: serverClockOffset(response) };
};

// Sends Razorpay Checkout's success response; the backend verifies it and books the appointment.
export const verifyPublicBookingPayment = async (intentId, bookingToken, checkoutResponse) => {
    const response = await publicAxiosClient.post(
        `${BASE_URL}/opd/public/booking-intents/${encodeURIComponent(intentId)}/verify-payment/`,
        {
            booking_token: bookingToken,
            razorpay_order_id: checkoutResponse.razorpay_order_id,
            razorpay_payment_id: checkoutResponse.razorpay_payment_id,
            razorpay_signature: checkoutResponse.razorpay_signature
        }
    );
    return response.data;
};

// Tests and health packages open for online booking, with the dates they can be booked for.
export const fetchPublicHealthPackages = async () => {
    const response = await publicAxiosClient.get(`${BASE_URL}/lab/public/health-packages/`);
    return response.data;
};

export const createPublicHealthPackageIntent = async (bookingData) => {
    const response = await publicAxiosClient.post(
        `${BASE_URL}/lab/public/health-package-intents/`,
        bookingData
    );
    return { ...response.data, clock_offset_ms: serverClockOffset(response) };
};

// Sends Razorpay Checkout's success response; the backend verifies it and bills the tests.
export const verifyPublicHealthPackagePayment = async (intentId, bookingToken, checkoutResponse) => {
    const response = await publicAxiosClient.post(
        `${BASE_URL}/lab/public/health-package-intents/${encodeURIComponent(intentId)}/verify-payment/`,
        {
            booking_token: bookingToken,
            razorpay_order_id: checkoutResponse.razorpay_order_id,
            razorpay_payment_id: checkoutResponse.razorpay_payment_id,
            razorpay_signature: checkoutResponse.razorpay_signature
        }
    );
    return response.data;
};

// Sends the patient's OPD receipt on WhatsApp for a confirmed public booking (once per booking).
export const sendPublicBookingInvoice = async (intentId, bookingToken, file) => {
    const formData = new FormData();
    formData.append('booking_token', bookingToken);
    formData.append('file', file, 'opd-receipt.pdf');
    const response = await publicAxiosClient.post(
        `${BASE_URL}/opd/public/booking-intents/${encodeURIComponent(intentId)}/send-invoice/`,
        formData
    );
    return response.data;
};

export const fetchNurses = async () => {
    const url = `${BASE_URL}/users/nurses/`;
    try {
        const response = await axiosClient.get(url);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        throw error;
    }
};

// This is appointment Id.
export const getPastAppointmentsById = async (appointmentId, ordering = 'created_at', queryParams = null) => {
    if (!appointmentId) throw new Error("Invalid appointment ID");

    try {
        // Fetch appointment details to get the patient ID
        const appointmentUrl = `${BASE_URL}/opd/appointments/${appointmentId}/`;
        const { data } = await axiosClient.get(appointmentUrl);
        const patientId = data?.patient_details?.id;

        if (!patientId) throw new Error("Patient ID not found");

        // Construct the URL with pagination and sorting params
        let historyUrl = `${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED`;

        // Add ordering if provided
        if (ordering) {
            historyUrl += `&ordering=${ordering}`;
        }

        // Add pagination params if provided
        if (queryParams) {
            historyUrl += `&${queryParams}`;
        }
        const historyResponse = await axiosClient.get(historyUrl);
        return historyResponse.data;
    } catch (error) {
        console.error("Error fetching past appointments:", error);
        openSnackbar({
            open: true,
            message: 'Failed to load appointment history',
            variant: 'alert',
            alert: {
                color: 'error',
                variant: 'filled'
            },
            close: true
        });
        throw error;
    }
};

export const fetchAppointmentsForPatient = async (patientId) => {
    const url = `${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED&limit=100`
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchAllAppointmentsForPatient = async (patientId, limit = 100) => {
    const url = `${BASE_URL}/opd/appointments/?patient_id=${patientId}&ordering=-created_at&limit=${limit}`;
    const response = await axiosClient.get(url);
    const data = response.data;
    return Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
};

export const createPrescriptionAppointment = async ({
    patientId,
    doctorId,
    prescriptionType,
    admissionId
}) => {
    const payload = {
        patient_id: patientId,
        doctor_id: doctorId,
        additional_notes: '',
        amount_paid: 0,
        payment_method: 'cash',
        created_at: dayjs().toISOString(),
        appointment_type: 'GENERAL',
        prescription_type: prescriptionType,
        admission_id: admissionId,
        additional_data: {
            prescription_type: prescriptionType,
            admission_id: admissionId
        }
    };
    const response = await axiosClient.post(`${BASE_URL}/opd/appointments/`, payload);
    return response.data;
};

export const calculateMedicineQuantity = (dosage, duration, frequency = 'Daily') => {
    let totalDosage = 0;
    
    if (dosage && typeof dosage === 'object') {
        totalDosage = Object.values(dosage).reduce((sum, dose) => sum + (parseFloat(dose) || 0), 0);
    } else if (typeof dosage === 'number') {
        totalDosage = dosage;
    } else if (typeof dosage === 'string') {
        totalDosage = parseFloat(dosage) || 0;
    }
    
    const durationInDays = parseInt(duration) || 0;
    
    if (!totalDosage || !durationInDays) return 1;
    
    switch (frequency) {
        case "On Alternate Days":
        return totalDosage * Math.ceil(durationInDays / 2);
        case "Two times a week on two consecutive days":
        return totalDosage * Math.ceil(durationInDays / 7) * 2;
        case "Two times a week 3 days apart":
        return totalDosage * Math.ceil(durationInDays / 7) * 2;
        case "Once a week":
        return totalDosage * Math.ceil(durationInDays / 7);
        case "SOS":
        return 0;
        case "Daily":
        default:
        return totalDosage * durationInDays;
    }
};

export const calculateTotalQuantity = (medicine) => {
    // If medicine has multiple dose schedules, sum across all schedules
    if (medicine.doseSchedules && medicine.doseSchedules.length > 0) {
        return medicine.doseSchedules.reduce((total, schedule) => {
        return total + calculateMedicineQuantity(
            schedule.dosage,
            schedule.duration,
            schedule.frequency
        );
        }, 0);
    }
    return calculateMedicineQuantity(medicine.dosage, medicine.duration, medicine.frequency);
};

export const normalizeMedicineFromAppointment = (med) => {
    const schedules =
      Array.isArray(med.doseSchedules) && med.doseSchedules.length > 0
        ? med.doseSchedules
        : [{
            dosage: med.dosage || { morning: 0, noon: 0, evening: 0, night: 0 },
            duration: med.duration || 0,
            frequency: med.frequency || "Daily",
            foodInstruction: med.foodInstruction || "",
          }];
  
    let calculatedQty = 0;
  
    schedules.forEach((s) => {
      const dailyDose = Object.values(s.dosage || {}).reduce(
        (sum, val) => sum + (parseFloat(val) || 0),
        0
      );
      calculatedQty += dailyDose * (parseInt(s.duration) || 0);
    });
  
    const totalQty = getDisplayQuantity(med);
    const finalQty =
      Number.isFinite(totalQty) && totalQty > 0
        ? totalQty
        : calculatedQty;
  
    const hasDiscount =
      med.discount !== null &&
      med.discount !== undefined &&
      med.discount !== "";
  
    const parsedDiscount = hasDiscount ? Number(med.discount) : null;
  
    return {
      medicine_id: med.id || null,
      medicine_name:
        med.product_name || med.medicine_name || med.name || "",
      quantity: finalQty,
      doseSchedules: schedules,
      dosage_string: schedules
        .map((s, idx) => {
          const d = s.dosage || {};
          return `#${idx + 1}: ${d.morning || 0}-${d.noon || 0}-${d.evening || 0}-${d.night || 0} (${s.duration || 0}d)`;
        })
        .join(" | "),
      instructions:
        med.notes || schedules[0]?.foodInstruction || "",
      discount: hasDiscount
        ? Number.isFinite(parsedDiscount)
          ? parsedDiscount
          : 0
        : null,
      is_custom: !med.id,
    };
};

export const fetchLatestPrescriptionForPatient = async (patientId, withinDays = 7, includeInProgress = true) => {
    try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - withinDays);

        const formattedStartDate = new Intl.DateTimeFormat('en-CA').format(startDate);
        endDate.setDate(endDate.getDate() + 1);
        const formattedEndDatePlusOne = new Intl.DateTimeFormat('en-CA').format(endDate);

        let latestAppointment = null;

        if (includeInProgress) {
            // Make two requests - one for COMPLETED, one for IN_PROGRESS, then pick the most recent
            try {
                const [completedResponse, inProgressResponse] = await Promise.all([
                    axiosClient.get(`${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED&ordering=-created_at&created_at_after=${formattedStartDate}&created_at_before=${formattedEndDatePlusOne}&limit=1`),
                    axiosClient.get(`${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=IN_PROGRESS&ordering=-created_at&created_at_after=${formattedStartDate}&created_at_before=${formattedEndDatePlusOne}&limit=1`)
                ]);

                const completedAppointments = completedResponse.data.results || [];
                const inProgressAppointments = inProgressResponse.data.results || [];

                // Combine and sort by created_at, pick the most recent
                const allAppointments = [...completedAppointments, ...inProgressAppointments];
                if (allAppointments.length > 0) {
                    allAppointments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    latestAppointment = allAppointments[0];
                }
            } catch (error) {
                // Fallback to single request if parallel requests fail
                console.warn('Parallel request failed, falling back to single request:', error);
                const response = await axiosClient.get(`${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED&ordering=-created_at&created_at_after=${formattedStartDate}&created_at_before=${formattedEndDatePlusOne}&limit=1`);
                if (response.data.results && response.data.results.length > 0) {
                    latestAppointment = response.data.results[0];
                }
            }
        } else {
            const response = await axiosClient.get(`${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED&ordering=-created_at&created_at_after=${formattedStartDate}&created_at_before=${formattedEndDatePlusOne}&limit=1`);
            if (response.data.results && response.data.results.length > 0) {
                latestAppointment = response.data.results[0];
            }
        }

        if (latestAppointment) {
            const medicines = latestAppointment.medicines_prescribed || [];
            
            const formattedMedicines = medicines.map(med => {
                const dosageObj = med.dosage || { morning: 0, noon: 0, evening: 0, night: 0 };
                const durationVal = parseInt(med.duration) || 0;
                const frequencyVal = med.frequency || 'Daily';
                const calculatedQty = calculateMedicineQuantity(dosageObj, durationVal, frequencyVal);
                
                return {
                    medicine_id: med.id || null,
                    medicine_name: med.product_name || med.medicine_name || med.name || '',
                    quantity: calculatedQty,
                    dosage: dosageObj,
                    dosage_string: `${dosageObj.morning || 0}-${dosageObj.noon || 0}-${dosageObj.evening || 0}-${dosageObj.night || 0}`,
                    duration: durationVal,
                    frequency: frequencyVal,
                    instructions: med.notes || med.foodInstruction || '',
                    discount: parseFloat(med.discount) || 0,
                    is_custom: med.id === null || med.id === undefined || med.id === ''
                };
            });
            
            return {
                found: formattedMedicines.length > 0,
                appointment: latestAppointment,
                medicines: formattedMedicines
            };
        }

        return {
            found: false,
            appointment: null,
            medicines: []
        };
    } catch (error) {
        console.error('Error fetching latest prescription:', error);
        return {
            found: false,
            appointment: null,
            medicines: []
        };
    }
};

// New function to fetch latest prescription with lab tests
export const fetchLatestPrescriptionWithLabTests = async (patientId, withinDays = 7, includeInProgress = true) => {
    try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - withinDays);

        const formattedStartDate = new Intl.DateTimeFormat('en-CA').format(startDate);
        endDate.setDate(endDate.getDate() + 1);
        const formattedEndDatePlusOne = new Intl.DateTimeFormat('en-CA').format(endDate);

        let latestAppointment = null;

        if (includeInProgress) {
            // Make two requests - one for COMPLETED, one for IN_PROGRESS, then pick the most recent
            try {
                const [completedResponse, inProgressResponse] = await Promise.all([
                    axiosClient.get(`${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED&ordering=-created_at&created_at_after=${formattedStartDate}&created_at_before=${formattedEndDatePlusOne}&limit=1`),
                    axiosClient.get(`${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=IN_PROGRESS&ordering=-created_at&created_at_after=${formattedStartDate}&created_at_before=${formattedEndDatePlusOne}&limit=1`)
                ]);

                const completedAppointments = completedResponse.data.results || [];
                const inProgressAppointments = inProgressResponse.data.results || [];

                // Combine and sort by created_at, pick the most recent
                const allAppointments = [...completedAppointments, ...inProgressAppointments];
                if (allAppointments.length > 0) {
                    allAppointments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    latestAppointment = allAppointments[0];
                }
            } catch (error) {
                // Fallback to single request if parallel requests fail
                console.warn('Parallel request failed, falling back to single request:', error);
                const response = await axiosClient.get(`${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED&ordering=-created_at&created_at_after=${formattedStartDate}&created_at_before=${formattedEndDatePlusOne}&limit=1`);
                if (response.data.results && response.data.results.length > 0) {
                    latestAppointment = response.data.results[0];
                }
            }
        } else {
            const response = await axiosClient.get(`${BASE_URL}/opd/appointments/?patient_id=${patientId}&status=COMPLETED&ordering=-created_at&created_at_after=${formattedStartDate}&created_at_before=${formattedEndDatePlusOne}&limit=1`);
            if (response.data.results && response.data.results.length > 0) {
                latestAppointment = response.data.results[0];
            }
        }

        if (latestAppointment) {
            const medicines = latestAppointment.medicines_prescribed || [];
            
            const labTests = latestAppointment.lab_package_prescribed || [];
            
            const formattedMedicines = medicines.map(med => {
                const dosageObj = med.dosage || { morning: 0, noon: 0, evening: 0, night: 0 };
                const durationVal = parseInt(med.duration) || 0;
                const frequencyVal = med.frequency || 'Daily';
                const calculatedQty = calculateMedicineQuantity(dosageObj, durationVal, frequencyVal);
                
                return {
                    medicine_id: med.id || null,
                    medicine_name: med.product_name || med.medicine_name || med.name || '',
                    quantity: calculatedQty,
                    dosage: dosageObj,
                    dosage_string: `${dosageObj.morning || 0}-${dosageObj.noon || 0}-${dosageObj.evening || 0}-${dosageObj.night || 0}`,
                    duration: durationVal,
                    frequency: frequencyVal,
                    instructions: med.notes || med.foodInstruction || '',
                    discount: parseFloat(med.discount) || 0,
                    is_custom: med.id === null || med.id === undefined || med.id === ''
                };
            });
            
            const formattedLabTests = labTests.map(test => {
                const hasDiscount =
                    test.discount !== null &&
                    test.discount !== undefined &&
                    test.discount !== '';
                const parsedDiscount = hasDiscount ? Number(test.discount) : null;

                return {
                    id: test.id || null,
                    name: test.name || '',
                    description: test.description || '',
                    price: parseFloat(test.price) || 0,
                    discount: hasDiscount && Number.isFinite(parsedDiscount) ? parsedDiscount : null,
                    is_custom: !test.id || test.id === null || test.id === undefined
                };
            });
            
            return {
                found: formattedMedicines.length > 0 || formattedLabTests.length > 0,
                appointment: latestAppointment,
                medicines: formattedMedicines,
                labTests: formattedLabTests
            };
        }

        return {
            found: false,
            appointment: null,
            medicines: [],
            labTests: []
        };
    } catch (error) {
        console.error('Error fetching latest prescription with lab tests:', error);
        return {
            found: false,
            appointment: null,
            medicines: [],
            labTests: []
        };
    }
};

export const fetchUserDetails = async (id) => {
    const url = `${BASE_URL}/users/search/?query=${id}`;
    try {
        const response = await axiosClient.get(url);
        // Handle paginated response format: {count, next, previous, results}
        if (response.data && response.data.results && response.data.results.length > 0) {
            return response.data.results[0];
        }
        // Fallback for array format (backward compatibility)
        if (Array.isArray(response.data) && response.data.length > 0) {
            return response.data[0];
        }
        return null;
    } catch (error) {
        throw error;
    }
}

export const fetchUsers = async (searchQuery, nextQueryURL, showOnlyEmployees) => {
    let url = `${BASE_URL}/users/details/?ordering=-updated_at`;
    if (showOnlyEmployees) {
        url += '&employee=true';
    }
    if (searchQuery && searchQuery.length > 0) {
        url = `${BASE_URL}/users/search/?query=${searchQuery}&ordering=-updated_at`;
        if (showOnlyEmployees) {
            url += '&employee=true';
        }
        if (nextQueryURL) {
            url += `&${nextQueryURL}`;
        }
    } else if (nextQueryURL) {
        url += `&${nextQueryURL}`;
    }
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// every employee, following the pages (capped so a runaway list cannot loop forever)
export const fetchAllEmployees = async (pageSize = 100, maxPages = 10) => {
    const employees = [];
    for (let page = 0; page < maxPages; page += 1) {
        const response = await fetchUsers('', `limit=${pageSize}&offset=${page * pageSize}`, true);
        employees.push(...(response?.results || []));
        if (!response?.next) break;
    }
    return employees;
}

export const fetchAuditLogs = async (pagination, searchQuery = '') => {
    const params = new URLSearchParams(pagination || 'limit=100&offset=0');
    if (searchQuery) params.set('search', searchQuery);
    const response = await axiosClient.get(`${BASE_URL}/audit-logs/?${params.toString()}`);
    return response.data;
}


export const deleteUsers = async (id) => {
    const url = `${BASE_URL}/users/update/?id=${id}`;
    try {
        const response = await axiosClient.delete(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}
export const createUser = async (data) => {
    const url = `${BASE_URL}/users/update/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const EditUser = async (data) => {
    const url = `${BASE_URL}/users/update/`;
    try {
        const response = await axiosClient.put(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

const normalizeBookingDate = (value) => {
    if (!value) return value;

    const stringValue = String(value);
    const isoDate = stringValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDate) return isoDate[1];

    const parsedDate = dayjs(value);
    return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : stringValue;
};

const normalizeBookingTime = (value) => {
    if (!value) return null;

    const stringValue = String(value).trim();
    const twelveHourTime = stringValue.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!twelveHourTime) return stringValue;

    let hours = Number(twelveHourTime[1]) % 12;
    if (twelveHourTime[3].toLowerCase() === 'pm') hours += 12;
    return `${String(hours).padStart(2, '0')}:${twelveHourTime[2]}`;
};

const normalizeDateOverride = (override) => ({
    ...override,
    date: normalizeBookingDate(override.date),
    start_time: normalizeBookingTime(override.start_time),
    end_time: normalizeBookingTime(override.end_time)
});

export const fetchDoctorBookingProfile = async (doctorId) => {
    const response = await axiosClient.get(`${BASE_URL}/users/doctors/${doctorId}/booking-profile/`);
    return {
        ...response.data,
        date_overrides: (response.data.date_overrides || []).map(normalizeDateOverride)
    };
}

export const updateDoctorBookingProfile = async (doctorId, bookingProfile, profilePhoto) => {
    const formData = new FormData();
    const scalarFields = [
        'practice_start_year',
        'consultation_fee',
        'old_patient_fee',
        'follow_up_fee',
        'online_booking_enabled',
        'slot_duration_minutes',
        'max_online_bookings_per_day'
    ];

    // Optional numbers can be cleared (the backend reads '' as null, e.g. back to the
    // hospital's default fee); other blank fields are left out so the saved value is kept.
    const clearableFields = ['practice_start_year', 'consultation_fee', 'old_patient_fee', 'follow_up_fee'];
    scalarFields.forEach((field) => {
        const value = bookingProfile[field];
        if (value === null || value === undefined || value === '') {
            if (clearableFields.includes(field)) formData.append(field, '');
            return;
        }
        formData.append(field, value);
    });
    formData.append(
        'weekly_availability',
        JSON.stringify(
            (bookingProfile.weekly_availability || []).filter((range) => (
                range.weekday !== '' && range.start_time && range.end_time
            ))
        )
    );
    formData.append(
        'date_overrides',
        JSON.stringify(
            (bookingProfile.date_overrides || [])
                .filter((override) => override.date)
                .map(normalizeDateOverride)
        )
    );
    if (profilePhoto) formData.append('profile_photo', profilePhoto);

    const response = await axiosClient.put(
        `${BASE_URL}/users/doctors/${doctorId}/booking-profile/`,
        formData
    );
    return response.data;
}

export const resetPassword = async (data) => {
    const url = `${BASE_URL}/users/reset_password/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const dailyCollectionReport = async (date, nextQueryURL, fetchAll = false) => {
    const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
    let url = `${BASE_URL}/accounting/collection/receptionist/?date=${formattedDate}`;

    if (fetchAll) {
        url += '&limit=10000000&offset=0';
    } else if (nextQueryURL) {
        url += `&${nextQueryURL}`;
    } else {
        url += '&limit=100&offset=0';
    }

    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const settleCollection = async (id, cash_amount, online_amount, date) => {
    const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
    const url = `${BASE_URL}/accounting/collection/receptionist/${id}/settle_cash/`;
    try {
        const response = await axiosClient.post(url, { cash_amount, online_amount, date: formattedDate });
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const fetchDailyTransition = async (dateToFetch, mode, nextQueryURL, fetchAll = false, customLimit = null, invoiceNumber = null) => {
    try {
        let url = `${BASE_URL}/accounting/ledger_invoice?`;

        if (invoiceNumber) {
            // When searching by invoice number, don't filter by date or payment method
            url += `invoice_number=${encodeURIComponent(invoiceNumber)}`;
        } else {
            // Normal filtering by date and optionally payment method when not searching
            const formattedDate = new Intl.DateTimeFormat('en-CA').format(dateToFetch);
            url += `invoice_created_at=${formattedDate}`;

            // Only add payment_method filter if mode is provided (not null)
            if (mode !== null) {
                url += `&payment_method=${mode}`;
            }
        }

        if (nextQueryURL) {
            url += `&${nextQueryURL}`;
        } else if (customLimit) {
            url += `&limit=${customLimit}&offset=0`;
        } else if (fetchAll) {
            url += '&limit=1000000&offset=0';
        } else {
            url += '&limit=100&offset=0';
        }

        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching transactions:', error);
        throw error;
    }
};

export const changeStatus = async (id, data) => {
    const url = `${BASE_URL}/opd/appointments/${id}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const fetchInventory = async (storeId, searchQuery, nextQueryURL, expiryParams = {}, ordering = 'expiry_date') => {
    const baseUrl = `${BASE_URL}/accounting/inventory/`;

    const queryItems = [];

    if (storeId) queryItems.push(`store=${storeId}`);
    if (searchQuery) queryItems.push(`search=${searchQuery}`);

    if (expiryParams.expiry !== undefined) {
        queryItems.push(`expiry=${expiryParams.expiry}`);
    }
    if (expiryParams.near_expiry !== undefined) {
        queryItems.push(`near_expiry=${expiryParams.near_expiry}`);
    }

    queryItems.push(`ordering=${ordering}`);
    if (nextQueryURL) queryItems.push(nextQueryURL);

    const queryString = queryItems.join('&');
    const url = `${baseUrl}?${queryString}`;

    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchSubstances = async (searchQuery) => {
    const url = `${BASE_URL}/pharmacy/substances/?search=${searchQuery}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const fetchGenerics = async (searchQuery) => {
    const url = `${BASE_URL}/pharmacy/generics/?search=${searchQuery}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const fetchGeneric = async (id) => {
    const url = `${BASE_URL}/pharmacy/generics/${id}/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const createSubstance = async (data) => {
    const url = `${BASE_URL}/pharmacy/substances/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const fetchSaltInteractions = async ({ limit = 10, offset = 0, search = '' } = {}) => {
    const response = await axiosClient.get(`${BASE_URL}/pharmacy/salt-interactions/`, {
        params: { limit, offset, ...(search ? { search } : {}) }
    });
    return response.data;
};

export const fetchSaltInteractionChoices = async () => {
    const response = await axiosClient.get(`${BASE_URL}/pharmacy/salt-interactions/choices/`);
    return response.data || [];
};

export const createSaltInteraction = async (data) => {
    const response = await axiosClient.post(`${BASE_URL}/pharmacy/salt-interactions/`, data);
    return response.data;
};

export const updateSaltInteraction = async (id, data) => {
    const response = await axiosClient.patch(`${BASE_URL}/pharmacy/salt-interactions/${id}/`, data);
    return response.data;
};

export const deleteSaltInteraction = async (id) => {
    await axiosClient.delete(`${BASE_URL}/pharmacy/salt-interactions/${id}/`);
};


export const addGeneric = async (data) => {
    const url = `${BASE_URL}/pharmacy/generics/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const updateGeneric = async (id, data) => {
    const url = `${BASE_URL}/pharmacy/generics/${id}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const allSpecializations = [
    { id: 1, name: "Anaesthetist" },
    { id: 2, name: "Ayurvedic Physician" },
    { id: 3, name: "Cardiologist" },
    { id: 4, name: "Consultant Critical Care" },
    { id: 5, name: "CTVS Surgeon" },
    { id: 6, name: "Dentist" },
    { id: 7, name: "Dermatologist" },
    { id: 8, name: "Diabetologist" },
    { id: 46, name: "Dietician" },
    { id: 9, name: "EMO (Emergency Medical Officer)" },
    { id: 10, name: "ENT Surgeon" },
    { id: 45, name: "Endocrinologist" },
    { id: 11, name: "Eye Surgeon" },
    { id: 12, name: "Gastro Surgeon" },
    { id: 13, name: "Gastroenterologist" },
    { id: 14, name: "General Physician" },
    { id: 15, name: "General Surgeon" },
    { id: 16, name: "Gynaecologist" },
    { id: 17, name: "Homeopathic Physician" },
    { id: 18, name: "Intensivist" },
    { id: 19, name: "Interventional Cardiologist" },
    { id: 20, name: "Maxillofacial Surgeon" },
    { id: 21, name: "Nephrologist" },
    { id: 22, name: "Neuro Physician" },
    { id: 23, name: "Neuro Surgeon" },
    { id: 24, name: "Oncologist" },
    { id: 25, name: "Onco Surgeon" },
    { id: 26, name: "Ophthalmologist" },
    // { id: 27, name: "Optometrist" },
    { id: 28, name: "Oral and Maxillofacial Surgeon" },
    { id: 29, name: "Orthopaedic Surgeon" },
    { id: 30, name: "Paediatrician" },
    { id: 31, name: "Pathologist" },
    { id: 32, name: "Physician Specialist" },
    { id: 33, name: "Physiotherapist" },
    { id: 34, name: "Plastic Surgeon" },
    { id: 35, name: "Psychiatrist" },
    { id: 36, name: "Psychologist" },
    { id: 37, name: "Public Health" },
    { id: 38, name: "Radiologist" },
    { id: 39, name: "RMO (Resident Medical Officer)" },
    { id: 40, name: "Sonologist" },
    { id: 41, name: "Spine Surgeon" },
    { id: 42, name: "Uro Surgeon" },
    { id: 43, name: "Urologist" },
    { id: 44, name: "External" },
];

export const allRoles = [
    {
        code: "receptionist",
        name: "Receptionist",
        category: "Administration & Operations"
    },
    {
        code: "doctor",
        name: "Doctor",
        category: "Clinical Care"
    },
    {
        code: "nurse",
        name: "Nurse",
        category: "Clinical Care"
    },
    {
        code: "optometrist",
        name: "Optometrist",
        category: "Clinical Care"
    },
    {
        code: "lab_billing",
        name: "Procedure Billing",
        category: "Finance & Billing"
    },
    {
        code: "senior_lab_technician",
        name: "Senior Lab Technician",
        category: "Laboratory"
    },
    {
        code: "lab_technician",
        name: "Other Lab Technician",
        category: "Laboratory"
    },
    {
        code: "lab_collection_staff",
        name: "Collection Staff",
        category: "Laboratory"
    },
    {
        code: "pharmacy_biller",
        name: "Pharmacy Biller",
        category: "Pharmacy"
    },
    {
        code: "it_technician",
        name: "IT Technician",
        category: "Administration & Operations"
    },
    {
        code: "superuser",
        name: "Superuser",
        category: "Administration & Operations"
    },
    // {
    //     code: "accountant",
    //     name: "Accountant"
    // },
    {
        code: "account_manager",
        name: "Account Manager",
        category: "Finance & Billing"
    },
    {
        code: "pharmacy_store_manager",
        name: "Pharmacy Store Manager",
        category: "Pharmacy"
    },
    {
        code: "radiologist",
        name: "Radiologist",
        category: "Clinical Care"
    }
].sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));

export const requestAbhaOtp = async (aadhaar, consent = null) => {
    const url = `${BASE_URL}/abha/api/v3/enrollment/generate_enrol_aadhaar_otp/`;
    try {
        const payload = { aadhaar };
        if (consent) {
            payload.consent = consent;
        }
        const response = await axiosClient.post(url, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const verifyAbhaOtp = async (txnId, otp, mobile) => {
    const url = `${BASE_URL}/abha/api/v3/enrollment/verify_enrol_aadhar_otp/`;
    try {
        const response = await axiosClient.post(url, {
            txn_id: txnId,
            otp: otp,
            mobile: mobile
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const generateAbhaSuggestions = async (txnId) => {
    const url = `${BASE_URL}/abha/api/v3/enrollment/generate_suggestions/`;
    try {
        const response = await axiosClient.get(url, {
            headers: {
                txnid: txnId,
                TXNID: txnId
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const registerAbhaAddress = async (abhaAddress, txnId) => {
    const url = `${BASE_URL}/abha/api/v3/enrollment/register_abha_address/`;
    try {
        const response = await axiosClient.post(url, {
            abhaAddress,
            txnId,
            preferred: 1
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/** ABHA M1 — send OTP to verify an existing ABHA (search / login). */
export const sendAbhaAuthOtp = async ({ login_type, login_id, verification_type }) => {
    const url = `${BASE_URL}/abha/api/v3/auth/send_otp/`;
    try {
        const response = await axiosClient.post(url, {
            login_type,
            login_id,
            verification_type
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/** ABHA M1 — verify OTP for existing ABHA login. */
export const verifyAbhaAuthOtp = async ({ txn_id, otp, verification_type, login_type }) => {
    const url = `${BASE_URL}/abha/api/v3/auth/verify_otp/`;
    try {
        const response = await axiosClient.post(url, {
            txn_id,
            otp,
            verification_type,
            login_type
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const verifyAbhaMobileUserSelection = async ({ txn_id, abha_number, t_token }) => {
    const url = `${BASE_URL}/abha/api/v3/auth/verify_mobile_user_selection/`;
    try {
        const response = await axiosClient.post(url, {
            txn_id,
            abha_number,
            t_token
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/** Existing patient registered with this ABHA number, if any: { exists, patient? }. */
/** sync_patient payload from a verified ABHA profile (formatAbhaProfile shape). */
export const abhaSyncPayload = (profile) => ({
    fullName: [profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(' ').trim(),
    contact: profile?.mobile || '',
    gender: profile?.gender || '',
    dob: profile?.dateOfBirth?.format ? profile.dateOfBirth.format('YYYY-MM-DD') : (profile?.dob || ''),
    email: profile?.email || '',
    pincode: profile?.pincode || '',
    village: profile?.village || '',
    address: profile?.address || '',
    state: profile?.state || '',
    abhaNumber: profile?.abhaNumber || '',
    abhaAddress: profile?.abhaAddress || ''
});

/**
 * Link a verified ABHA to an existing HMS patient. With useAbhaDetails
 * ("Go with ABDM") the patient's details are replaced by the ABHA profile's;
 * without ("Go with HIMS") only the ABHA number / address are linked.
 */
export const syncPatientWithAbha = async (profile, { patientId, useAbhaDetails = true } = {}) => {
    const url = `${BASE_URL}/abha/api/v3/profile/sync_patient/`;
    const response = await axiosClient.post(url, {
        ...abhaSyncPayload(profile),
        patient_id: patientId || undefined,
        update_demographics: useAbhaDetails
    });
    return response.data;
};

export const findPatientByAbhaNumber = async (abhaNumber) => {
    const url = `${BASE_URL}/abha/api/v3/profile/patient_by_abha_number/`;
    const response = await axiosClient.get(url, { params: { abha_number: abhaNumber } });
    return response.data;
};

/** All ABHA addresses of a patient, preferred first. */
export const fetchPatientAbhaAddresses = async (patientId) => {
    const url = `${BASE_URL}/abha/api/v3/profile/abha_addresses/`;
    const response = await axiosClient.get(url, { params: { patient_id: patientId } });
    return response.data?.abha_addresses || [];
};

/**
 * ABHA addresses registered in HMS containing ``query`` (2+ characters), with
 * the patient (UHID) each belongs to: [{ abha_address, patient_id, patient_name }].
 */
export const searchAbhaAddresses = async (query) => {
    const url = `${BASE_URL}/abha/api/v3/profile/search_abha_addresses/`;
    const response = await axiosClient.get(url, { params: { q: query } });
    return response.data?.results || [];
};

/** Attach another ABHA address to an existing patient (same UHID). */
export const addPatientAbhaAddress = async ({ patientId, abhaAddress, abhaNumber, makePreferred = false }) => {
    const url = `${BASE_URL}/abha/api/v3/profile/add_abha_address/`;
    const response = await axiosClient.post(url, {
        patient_id: patientId,
        abha_address: abhaAddress,
        abha_number: abhaNumber,
        make_preferred: makePreferred
    });
    return response.data;
};

/** Pick which of the patient's ABHA addresses is used for ABDM linking. */
export const setPreferredAbhaAddress = async ({ patientId, abhaAddress }) => {
    const url = `${BASE_URL}/abha/api/v3/profile/set_preferred_abha_address/`;
    const response = await axiosClient.post(url, { patient_id: patientId, abha_address: abhaAddress });
    return response.data;
};

/** HMS patients with this mobile number who have an ABHA number (mobile verification flow). */
export const findPatientsByMobile = async (mobile) => {
    const url = `${BASE_URL}/abha/api/v3/profile/patients_by_mobile/`;
    const response = await axiosClient.get(url, { params: { mobile } });
    return response.data?.patients || [];
};

/** Change a verified ABHA's mobile number: OTP to the new number. Returns { txnId, message }. */
export const requestAbhaMobileUpdateOtp = async ({ xToken, mobile }) => {
    const url = `${BASE_URL}/abha/api/v3/profile/update_mobile_request_otp/`;
    const response = await axiosClient.post(url, { x_token: xToken, mobile });
    return response.data;
};

/** Verify that OTP; on success ABDM and the HMS patient carry the new number. */
export const verifyAbhaMobileUpdate = async ({ xToken, txnId, otp, mobile, abhaNumber }) => {
    const url = `${BASE_URL}/abha/api/v3/profile/update_mobile_verify/`;
    const response = await axiosClient.post(url, {
        x_token: xToken,
        txn_id: txnId,
        otp,
        mobile,
        abha_number: abhaNumber
    });
    return response.data;
};

/** ABHA M1 — fetch profile / card context after auth (Bearer session or X-Token). */
export const fetchAbhaProfile = async (xToken) => {
    const url = `${BASE_URL}/abha/api/v3/profile/get_profile_details/`;
    try {
        const config = xToken
            ? { headers: { 'X-Token': xToken } }
            : {};
        const response = await axiosClient.get(url, config);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const downloadAbhaCard = async (xToken) => {
    const url = `${BASE_URL}/abha/api/v3/profile/download_abha_card/`;
    try {
        const response = await axiosClient.get(url, {
            headers: {
                'X-Token': xToken
            },
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Helper function to format ABHA profile data for customer form
export const formatAbhaProfile = (abhaProfile) => {
    let dateOfBirth = null;
    if (abhaProfile.dob) {
        dateOfBirth = dayjs(abhaProfile.dob, 'DD-MM-YYYY');
    } else if (abhaProfile.yearOfBirth) {
        const yyyy = abhaProfile.yearOfBirth;
        const mm = abhaProfile.monthOfBirth || '01';
        const dd = abhaProfile.dayOfBirth || '01';
        dateOfBirth = dayjs(`${yyyy}-${mm}-${dd}`, 'YYYY-MM-DD');
    }

    const address = abhaProfile.address || '';
    const village = abhaProfile.village || (address ? address.split(',')[0].trim() : '') || abhaProfile.townName || '';
    const pincode = abhaProfile.pincode || abhaProfile.pinCode || '';

    return {
        firstName: abhaProfile.firstName || '',
        lastName: abhaProfile.lastName || '',
        middleName: abhaProfile.middleName || '',
        dateOfBirth: dateOfBirth,
        gender: (function() {
            const g = (abhaProfile.gender || '').trim().toUpperCase();
            if (g === 'M' || g === 'MALE') return 'Male';
            if (g === 'F' || g === 'FEMALE') return 'Female';
            if (g === 'O' || g === 'OTHER') return 'Other';
            return '';
        })(),
        mobile: abhaProfile.mobile || '',
        email: abhaProfile.email || '',
        address: address,
        pincode: pincode,
        village: village,
        district: abhaProfile.districtName || abhaProfile.district || '',
        state: abhaProfile.stateName || abhaProfile.state || '',
        abhaNumber: abhaProfile.ABHANumber || abhaProfile.healthIdNumber || abhaProfile.abhaNumber || '',
        abhaAddress: abhaProfile.preferredAbhaAddress || abhaProfile.abhaAddress
            || (Array.isArray(abhaProfile.phrAddress) ? abhaProfile.phrAddress[0] : '') || '',
        photo: abhaProfile.profilePhoto || abhaProfile.photo || '',
        kycVerified: abhaProfile.kycVerified,
        kycStatus: abhaProfile.kycStatus || ''
    };
};

export const orderLabTests = async (payload) => {
    const url = `${BASE_URL}/lab/lab-tests/order_test/`;
    try {
        const response = await axiosClient.post(url, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchMedicineBatches = async (medicineId, storeId) => {
    const url = `${BASE_URL}/accounting/inventory/?store=${storeId}&medicine=${medicineId}`;
    try {
        const response = await axiosClient.get(url);
        return response.data.results || [];
    } catch (error) {
        console.error('Error fetching medicine batches:', error);
        return [];
    }
};

export const discardExpiredStock = async (data) => {
    const url = `${BASE_URL}/accounting/inventory/discard_expired_stock/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateInventoryItem = async (itemId, data) => {
    const url = `${BASE_URL}/accounting/inventory/${itemId}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};


export const fetchNonMedicineBatches = async (nonMedicineItemId, storeId) => {
    const url = `${BASE_URL}/accounting/inventory/?store=${storeId}&non_medicine_item=${nonMedicineItemId}`;
    try {
        const response = await axiosClient.get(url);
        return response.data.results || [];
    } catch (error) {
        console.error('Error fetching non-medicine batches:', error);
        return [];
    }
};

function hasValidSellingPrice(batch) {
    const sellingPrice = parseFloat(batch.selling_price || 0);
    return (
        batch.selling_price &&
        batch.selling_price !== "None" &&
        batch.selling_price !== "null" &&
        batch.selling_price !== null &&
        !isNaN(sellingPrice) &&
        sellingPrice > 0
    );
}

const medicineDetailsCache = new Map();
const batchDataCache = new Map();

const pendingRequests = new Map();

const CACHE_TTL = 5 * 60 * 1000;

const getCachedMedicineDetails = (medicineId) => {
    const cached = medicineDetailsCache.get(medicineId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
    }
    return null;
};

const setCachedMedicineDetails = (medicineId, data) => {
    medicineDetailsCache.set(medicineId, {
        data,
        timestamp: Date.now()
    });
};

const getCachedBatchData = (key) => {
    const cached = batchDataCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
    }
    return null;
};

const setCachedBatchData = (key, data) => {
    batchDataCache.set(key, {
        data,
        timestamp: Date.now()
    });
};

export const clearExpiredCache = () => {
    const now = Date.now();

    for (const [key, value] of medicineDetailsCache.entries()) {
        if (now - value.timestamp > CACHE_TTL) {
            medicineDetailsCache.delete(key);
        }
    }

    for (const [key, value] of batchDataCache.entries()) {
        if (now - value.timestamp > CACHE_TTL) {
            batchDataCache.delete(key);
        }
    }
};
export const clearAllCache = () => {
    medicineDetailsCache.clear();
    batchDataCache.clear();
    pendingRequests.clear();
};

export const calculateSellingPriceWithFallback = async (medicineId, nonMedicineId, requestedQuantity, storeId, defaultPrice = 0, signal = null) => {
    try {
        let batchData = [];

        if (signal?.aborted) {
            throw new Error('Request cancelled');
        }

        const batchCacheKey = `${medicineId || nonMedicineId}_${storeId}`;
        const cachedBatchData = getCachedBatchData(batchCacheKey);

        if (cachedBatchData) {
            batchData = cachedBatchData;
        } else {
            const requestKey = `batch_${batchCacheKey}`;

            if (pendingRequests.has(requestKey)) {
                batchData = await pendingRequests.get(requestKey);
            } else {
                const requestPromise = (async () => {
                    let data = [];
                    if (nonMedicineId) {
                        data = await fetchNonMedicineBatches(nonMedicineId, storeId);
                    } else if (medicineId) {
                        data = await fetchMedicineBatches(medicineId, storeId);
                    }
                    setCachedBatchData(batchCacheKey, data);
                    pendingRequests.delete(requestKey);
                    return data;
                })();

                pendingRequests.set(requestKey, requestPromise);
                batchData = await requestPromise;
            }
        }

        if (signal?.aborted) {
            throw new Error('Request cancelled');
        }

        if (!batchData.length) {
            return {
                finalPrice: defaultPrice,
                hasFallback: true,
                quantityCovered: 0,
                fallbackQuantity: requestedQuantity,
                details: `No inventory found, using default price ₹${defaultPrice}`
            };
        }

        let originalMedicinePrice = defaultPrice;

        if (medicineId) {
            try {
                const cachedMedicineDetails = getCachedMedicineDetails(medicineId);

                if (cachedMedicineDetails) {
                    originalMedicinePrice = Number(cachedMedicineDetails.price) || defaultPrice;
                } else {
                    const requestKey = `medicine_${medicineId}`;

                    if (pendingRequests.has(requestKey)) {
                        const medicine = await pendingRequests.get(requestKey);
                        originalMedicinePrice = Number(medicine.price) || defaultPrice;
                    } else {
                        const requestPromise = axiosClient.get(`${BASE_URL}/pharmacy/medicines/${medicineId}/`)
                            .then(response => {
                                const medicine = response.data;
                                setCachedMedicineDetails(medicineId, medicine);
                                pendingRequests.delete(requestKey);
                                return medicine;
                            })
                            .catch(error => {
                                pendingRequests.delete(requestKey);
                                throw error;
                            });

                        pendingRequests.set(requestKey, requestPromise);
                        const medicine = await requestPromise;

                        if (medicine && medicine.price) {
                            originalMedicinePrice = Number(medicine.price);
                        } else {
                            originalMedicinePrice = defaultPrice;
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching original medicine price:', error);
                originalMedicinePrice = defaultPrice;
            }
        } else {
            originalMedicinePrice = defaultPrice;
        }

        // Process all batches and sort by expiry date (FIFO)
        const allBatches = batchData
            .reduce((acc, item) => {
                if (item.batch_details) {
                    acc.push(...item.batch_details);
                }
                return acc;
            }, [])
            .filter(batch => parseInt(batch.current_stock || 0) > 0)
            .sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date));

        if (!allBatches.length) {
            return {
                finalPrice: defaultPrice,
                hasFallback: true,
                quantityCovered: 0,
                fallbackQuantity: requestedQuantity,
                details: `No inventory found, using default price ₹${defaultPrice}`
            };
        }

        let remainingQuantity = requestedQuantity;
        let totalCost = 0;
        let quantityCovered = 0;
        let quantityFromSellingPrice = 0;
        let quantityFromDefaultPrice = 0;
        const batchBreakdown = [];

        // Process batches in FIFO order (by expiry date)
        for (const batch of allBatches) {
            if (remainingQuantity <= 0) {
                break;
            }

            const availableInBatch = Math.min(batch.current_stock, remainingQuantity);
            const batchHasSellingPrice = hasValidSellingPrice(batch);

            let batchPrice;
            let priceSource;

            if (batchHasSellingPrice) {
                batchPrice = parseFloat(batch.selling_price);
                priceSource = 'batch_selling_price';
                quantityFromSellingPrice += availableInBatch;
            } else {
                batchPrice = originalMedicinePrice;
                priceSource = 'original_medicine_price';
                quantityFromDefaultPrice += availableInBatch;
            }

            const batchCost = availableInBatch * batchPrice;

            totalCost += batchCost;
            quantityCovered += availableInBatch;
            remainingQuantity -= availableInBatch;

            batchBreakdown.push({
                batchNumber: batch.batch_number,
                quantity: availableInBatch,
                sellingPrice: batchPrice,
                source: priceSource,
                cost: batchCost,
                expiry: batch.expiry_date,
                stock: batch.current_stock
            });
        }

        // Handle any remaining quantity not covered by available stock
        const uncoveredQuantity = remainingQuantity;
        const hasFallback = uncoveredQuantity > 0;

        if (hasFallback) {
            const fallbackCost = uncoveredQuantity * originalMedicinePrice;
            totalCost += fallbackCost;
            quantityFromDefaultPrice += uncoveredQuantity;

            batchBreakdown.push({
                batchNumber: 'out_of_stock',
                quantity: uncoveredQuantity,
                sellingPrice: originalMedicinePrice,
                source: 'original_medicine_price_no_stock',
                cost: fallbackCost
            });
        }

        // Determine final price - use actual batch price if all quantity comes from one batch
        let finalPrice;
        let details = '';

        if (batchBreakdown.length === 1 && !hasFallback) {
            // Single batch fulfills entire quantity - use that batch's price directly
            finalPrice = batchBreakdown[0].sellingPrice;
            const batch = batchBreakdown[0];

            if (batch.source === 'batch_selling_price') {
                details = `${requestedQuantity} units from batch ${batch.batchNumber} @ ₹${finalPrice}`;
            } else {
                details = `${requestedQuantity} units from batch ${batch.batchNumber} @ original price ₹${finalPrice}`;
            }
        } else {
            // Multiple batches or fallback needed - calculate total cost per unit
            finalPrice = requestedQuantity > 0 ? totalCost / requestedQuantity : 0;

            if (quantityFromSellingPrice > 0 && quantityFromDefaultPrice > 0) {
                // Show detailed breakdown for mixed pricing
                const batchCosts = batchBreakdown.map(b => `${b.quantity}×₹${b.sellingPrice}=₹${b.cost}`).join(' + ');
                details = `Mixed batches: ${batchCosts} = ₹${totalCost.toFixed(2)} total (₹${finalPrice.toFixed(2)} per unit)`;
            } else if (quantityFromSellingPrice > 0) {
                details = `${quantityFromSellingPrice} units from batches with selling price = ₹${finalPrice.toFixed(2)} per unit`;
            } else {
                details = `${quantityFromDefaultPrice} units at original price ₹${originalMedicinePrice} = ₹${finalPrice.toFixed(2)} per unit`;
            }
        }

        return {
            finalPrice: Math.round(finalPrice * 100) / 100,
            hasFallback,
            quantityCovered,
            fallbackQuantity: uncoveredQuantity,
            batchBreakdown,
            details,
            quantityFromSellingPrice,
            quantityFromDefaultPrice
        };

    } catch (error) {
        console.error('Error calculating selling price:', error);
        return {
            finalPrice: defaultPrice,
            hasFallback: true,
            quantityCovered: 0,
            fallbackQuantity: requestedQuantity,
            details: `Error occurred, using default price ₹${defaultPrice}`
        };
    }
};

export const sellMedicines = async (payload) => {
    const url = `${BASE_URL}/accounting/inventory/process_sale/`;
    try {
        const response = await axiosClient.post(url, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const editInvoice = async (invoiceId, payload) => {
    const url = `${BASE_URL}/accounting/invoices/${invoiceId}/edit_invoice/`;
    try {
        const response = await axiosClient.post(url, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const sellMedicineToSupplier = async (payload) => {
    const url = `${BASE_URL}/accounting/inventory/process_sale_supplier/`;
    try {
        const response = await axiosClient.post(url, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchStores = async (nextQueryURL) => {
    const baseUrl = `${BASE_URL}/accounting/stores/`;
    const url = nextQueryURL ? `${baseUrl}?${nextQueryURL}` : baseUrl;

    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateStore = async (id, data) => {
    const url = `${BASE_URL}/accounting/stores/${id}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createStore = async (data) => {
    const url = `${BASE_URL}/accounting/stores/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchStoreInventory = async (storeId, searchQuery = '') => {
    const url = `${BASE_URL}/accounting/inventory/?store=${storeId}&ordering=expiry_date${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createStoreTransfer = async (data) => {
    const url = `${BASE_URL}/accounting/transfers/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const InventoryItemType = {
    MEDICAL: 'medical',
    NON_MEDICAL: 'non-medical'
}

export const fetchMedicalItems = async (searchQuery, isActive) => {
    const url = `${BASE_URL}/pharmacy/medicines/search/?q=${searchQuery}${isActive ? '&is_active=true' : ''}`;
    try {
        const response = await axiosClient.get(url);
        const items = Array.isArray(response.data) ? response.data : (response.data?.results || []);
        return items.map(item => ({
            id: item.id,
            name: item.product_name || '',
            hsn_code: item.hsn_code || '',
            type: InventoryItemType.MEDICAL,
            packaging_size: item.packaging_size || 1
        }));
    } catch (err) {
        return [];
    }
};

export const fetchNonMedicalItems = async (searchQuery, isActive) => {
    const url = `${BASE_URL}/pharmacy/non-medicine-items/search/?q=${searchQuery}${isActive ? '&is_active=true' : ''}`;
    try {
        const response = await axiosClient.get(url);
        const items = Array.isArray(response.data) ? response.data : (response.data?.results || []);
        return items.map(item => ({
            id: item.id,
            name: item.name,
            type: InventoryItemType.NON_MEDICAL,
            packaging_size: item.packaging_size || 1
        }));
    } catch (err) {
        return [];
    }
}
export const fetchNonMedicineItems = async (searchQuery) => {
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    const url = `${BASE_URL}/pharmacy/non-medicine-items/?${params.toString()}`;
    try {
        const response = await axiosClient.get(url);
        const items = Array.isArray(response.data) ? response.data : (response.data?.results || []);
        return items.map(item => ({
            ...item,
            type: InventoryItemType.NON_MEDICAL
        }));
    } catch (err) {
        return [];
    }
}

export const fetchInventoryBills = async (nextQueryURL) => {
    let url = `${BASE_URL}/accounting/invoices/?type=purchase`;
    if (nextQueryURL) url += `&${nextQueryURL}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchSaleBills = async (nextQueryURL) => {
    let url = `${BASE_URL}/accounting/inventory/get_sale_supplier_bills/`;
    if (nextQueryURL) url += `?${nextQueryURL}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchInventoryItemBillHistory = async ({ billType, itemId, itemType, limit = 10, offset = 0 }) => {
    const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const itemParam = itemType === 'medicine' ? 'medicine_id' : 'non_medicine_item_id';
    query.set(itemParam, String(itemId));

    const path = billType === 'sale'
        ? '/accounting/inventory/get_sale_supplier_bills/'
        : '/accounting/invoices/';
    if (billType !== 'sale') query.set('type', billType);

    const response = await axiosClient.get(`${BASE_URL}${path}?${query.toString()}`);
    return response.data;
}

export const cancelSaleBill = async (invoiceNumber) => {
    const url = `${BASE_URL}/accounting/invoices/${invoiceNumber}/`;
    try {
        const response = await axiosClient.delete(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchPharmacyInvoices = async (nextQueryURL, searchQuery, date = null, includeTotals = false, createdBy = null, store = null, customer = null) => {
    let url = `${BASE_URL}/accounting/invoices/?type=pharmacy`;
    if (nextQueryURL) url += `&${nextQueryURL}`;
    if (searchQuery) url += `&invoice_number=${searchQuery}`;
    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        url += `&date=${formattedDate}`;
    }
    if (includeTotals) url += `&include_totals=true`;
    if (createdBy) url += `&created_by=${createdBy}`;
    if (store) url += `&store=${store}`;
    if (customer) url += `&customer=${customer}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchInvoiceByInvoiceNumber = async (invoiceNumber) => {
    console.log(" debug Fetching invoice by number:", invoiceNumber);
    let url = `${BASE_URL}/accounting/invoices/`;
    if (invoiceNumber) url += `?invoice_number=${invoiceNumber}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}
export const fetchLabInvoices = async (nextQueryURL, searchQuery, date = null, includeTotals = false, createdBy = null, customer = null) => {
    let url = `${BASE_URL}/accounting/invoices/?type=lab`;
    if (nextQueryURL) url += `&${nextQueryURL}`;
    if (searchQuery) url += `&invoice_number=${searchQuery}`;
    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        url += `&date=${formattedDate}`;
    }
    if (includeTotals) url += `&include_totals=true`;
    if (createdBy) url += `&created_by=${createdBy}`;
    if (customer) url += `&customer=${customer}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// OPD consultation (appointment) bills: invoice numbers with the "O-" prefix
export const fetchOpdInvoices = async (nextQueryURL, searchQuery, date = null, includeTotals = false, createdBy = null, customer = null) => {
    let url = `${BASE_URL}/accounting/invoices/?type=opd`;
    if (nextQueryURL) url += `&${nextQueryURL}`;
    if (searchQuery) url += `&invoice_number=${searchQuery}`;
    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        url += `&date=${formattedDate}`;
    }
    if (includeTotals) url += `&include_totals=true`;
    if (createdBy) url += `&created_by=${createdBy}`;
    if (customer) url += `&customer=${customer}`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchUserCollectionTotals = async (userId = null, date = null) => {
    let url = `${BASE_URL}/accounting/invoices/?include_totals=true`;

    try {
        const response = await axiosClient.get(url);
        return {
            cash_collections: response.data.totals?.cash_total || 0,
            online_collections: response.data.totals?.online_total || 0,
            total_collections: (response.data.totals?.cash_total || 0) + (response.data.totals?.online_total || 0),
            total_invoices: response.data.totals?.total_invoices || 0,
            gst_collections: response.data.totals?.gst_total || response.data.totals?.total_gst || 0,
            cgst_collections: response.data.totals?.cgst_total || 0,
            sgst_collections: response.data.totals?.sgst_total || 0,
            igst_collections: response.data.totals?.igst_total || 0
        };
    } catch (error) {
        console.error('Error fetching user collection totals:', error);
        return {
            cash_collections: 0,
            online_collections: 0,
            total_collections: 0,
            total_invoices: 0,
            gst_collections: 0,
            cgst_collections: 0,
            sgst_collections: 0,
            igst_collections: 0
        };
    }
}

const HOSPITAL_DETAILS_SESSION_KEY = 'hospitalDetails';

const cacheHospitalDetails = (data) => {
    if (!data) return;

    try {
        sessionStorage.setItem(HOSPITAL_DETAILS_SESSION_KEY, JSON.stringify(data));
    } catch (error) {
        console.error('Failed to cache hospital settings:', error);
    }
};

export const fetchHospitalDetails = async ({ forceRefresh = false } = {}) => {
    if (!forceRefresh) {
        try {
            const cachedHospitalDetails = sessionStorage.getItem(HOSPITAL_DETAILS_SESSION_KEY);
            if (cachedHospitalDetails) return JSON.parse(cachedHospitalDetails);
        } catch (error) {
            console.error('Failed to read cached hospital settings:', error);
        }
    }

    const url = `${BASE_URL}/users/hospital/settings/`;
    try {
        const response = await axiosClient.get(url);
        cacheHospitalDetails(response.data);
        return response.data;
    } catch (error) {
        return null;
    }
};

// Hospital settings for public pages; never sends the staff token or redirects to login.
export const fetchPublicHospitalDetails = async () => {
    try {
        const cachedHospitalDetails = sessionStorage.getItem(HOSPITAL_DETAILS_SESSION_KEY);
        if (cachedHospitalDetails) return JSON.parse(cachedHospitalDetails);
    } catch (error) {
        console.error('Failed to read cached hospital settings:', error);
    }

    try {
        const response = await publicAxiosClient.get(`${BASE_URL}/users/hospital/settings/`);
        cacheHospitalDetails(response.data);
        return response.data;
    } catch (error) {
        return null;
    }
};

export const updateHospitalDetails = async (data) => {
    const url = `${BASE_URL}/users/hospital/settings/`;
    try {
        const response = await axiosClient.patch(url, data);
        cacheHospitalDetails(response.data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchWholesalers = async (nextQueryURL) => {
    const baseUrl = `${BASE_URL}/accounting/wholesaler/`;
    const url = nextQueryURL ? `${baseUrl}?${nextQueryURL}` : baseUrl;

    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const createWholesaler = async (data) => {
    const url = `${BASE_URL}/accounting/wholesaler/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const updateWholesaler = async (id, data) => {
    const url = `${BASE_URL}/accounting/wholesaler/${id}/`;
    try {
        const response = await axiosClient.patch(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const purchaseItemsFromWholesaler = async (data) => {
    const url = `${BASE_URL}/accounting/inventory/process_purchase/`;
    try {
        const response = await axiosClient.post(url, data);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const cancelInvoiceItems = async (invoiceNumber, payload) => {
    const url = `${BASE_URL}/accounting/invoices/${invoiceNumber}/`;
    try {
        const response = await axiosClient.delete(url, { data: payload });
        if (response.status === 202) {
            return { ...response.data, requiresCancellationOtp: true };
        }
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchBillDetails = async (formData) => {
    const url = `${BASE_URL}/ml/upload-invoice/`;
    try {
        const response = await axiosClient.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchCancellationLedger = async (searchQuery, nextQueryURL, fetchAll = false, cancelled_by = null, invoice = null, date = null) => {
    let url = `${BASE_URL}/accounting/cancellation-ledger/?`;
    const params = [];

    if (date) {
        const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
        params.push(`date=${formattedDate}`);
    }

    if (searchQuery) {
        params.push(`invoice_id=${encodeURIComponent(searchQuery)}`);
    }

    if (cancelled_by) {
        params.push(`cancelled_by=${encodeURIComponent(cancelled_by)}`);
    }

    if (invoice) {
        params.push(`invoice=${encodeURIComponent(invoice)}`);
    }

    if (fetchAll) {
        params.push('limit=10000000');
        params.push('offset=0');
    } else if (nextQueryURL) {
        const filterParams = [];
        if (date) {
            const formattedDate = new Intl.DateTimeFormat('en-CA').format(date);
            if (!nextQueryURL.includes('date=')) filterParams.push(`date=${formattedDate}`);
        }
        if (searchQuery && !nextQueryURL.includes('invoice_id=')) {
            filterParams.push(`invoice_id=${encodeURIComponent(searchQuery)}`);
        }
        if (cancelled_by && !nextQueryURL.includes('cancelled_by=')) {
            filterParams.push(`cancelled_by=${encodeURIComponent(cancelled_by)}`);
        }
        if (invoice && !nextQueryURL.includes('invoice=')) {
            filterParams.push(`invoice=${encodeURIComponent(invoice)}`);
        }

        if (filterParams.length > 0) {
            url += `${nextQueryURL}&${filterParams.join('&')}`;
        } else {
            url += `${nextQueryURL}`;
        }
    } else {
        params.push('limit=100');
        params.push('offset=0');
    }

    if (!nextQueryURL) {
        url += params.join('&');
    }

    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchStoreTransfers = async (nextQueryURL, fetchAll = false, dateFrom = null, dateTo = null, toStore = null) => {
    let url = `${BASE_URL}/accounting/transfers/?`;
    const params = ['ordering=-created_at'];
    if (dateFrom) {
        params.push(`date_from=${dateFrom}`);
    }
    if (dateTo) {
        params.push(`date_to=${dateTo}`);
    }
    if (toStore) {
        params.push(`to_store=${toStore}`);
    }

    if (fetchAll) {
        params.push('limit=10000000&offset=0');
    } else if (nextQueryURL) {
        if (params.length > 0 && !nextQueryURL.includes('date_from') && !nextQueryURL.includes('date_to')) {
            url += `${nextQueryURL}&${params.join('&')}`;
        } else {
            url += `${nextQueryURL}`;
        }
    } else {
        params.push('limit=100&offset=0');
    }

    if (!nextQueryURL) {
        url += params.join('&');
    }

    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get current logged in user details
export const getCurrentUser = async () => {
    try {
        const response = await axiosClient.get('/users/');
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Resend OTP for discount approval
export const resendDiscountOTP = async (messageId) => {
    try {
        const response = await axiosClient.post('/accounting/invoices/resent_discount_otp/', {
            message_id: messageId
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchUserDiscountInfo = async (type, userId) => {
    try {
        const response = await axiosClient.get(
            `/accounting/invoices/discount_information/?type=${type}&user=${userId}`
        );
        const discount = response.data;
        return {
            discount: typeof discount === 'number' ? discount : null,
            hasUserDiscount: typeof discount === 'number' && discount > 0
        };
    } catch (error) {
        console.error('Error fetching user discount:', error);
        return { discount: null, hasUserDiscount: false };
    }
};

// Data Export API function
export const exportAllData = async (dataType, startDate = null, endDate = null) => {
    try {
        const params = new URLSearchParams();
        if (dataType) params.append('data_type', dataType);
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);

        const url = `/accounting/invoices/export_data/?${params.toString()}`;

        const response = await axiosClient.get(url, {
            responseType: 'blob'
        });

        return response.data;
    } catch (error) {
        console.error('Error exporting data:', error);
        throw error;
    }
};

// Fetch export configuration (supported data types and descriptions)
export const fetchExportConfig = async () => {
    try {
        const url = `/accounting/invoices/export_data/`;
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching export config:', error);
        throw error;
    }
};


// Fetch dashboard data
export const fetchDashboardData = async (startDate, endDate) => {
    try {
        let url = `${BASE_URL}/opd/dashboard/`;
        const params = new URLSearchParams();

        if (startDate) {
            params.append('start_date', startDate);
        }
        if (endDate) {
            params.append('end_date', endDate);
        }

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching dashboard data:', error);
        throw error;
    }
};

export const fetchDoctorMetrics = async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const query = params.toString();
    const response = await axiosClient.get(
        `${BASE_URL}/opd/dashboard/doctor_metrics/${query ? `?${query}` : ''}`
    );
    return response.data;
};

export const fetchTatMetrics = async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const query = params.toString();
    const response = await axiosClient.get(
        `${BASE_URL}/opd/dashboard/tat_metrics/${query ? `?${query}` : ''}`
    );
    return response.data;
};

export const fetchLabTestChart = async (group, subGroup, startDate, endDate, aggregateBy) => {
    try {
        let url = `${BASE_URL}/opd/dashboard/lab_test_chart/`;
        const params = new URLSearchParams();

        if (group) {
            params.append('group', group);
        }
        if (subGroup) {
            params.append('sub_group', subGroup);
        }
        if (startDate) {
            params.append('start_date', startDate);
        }
        if (endDate) {
            params.append('end_date', endDate);
        }
        if (aggregateBy) {
            params.append('aggregate_by', aggregateBy);
        }

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching lab test chart:', error);
        throw error;
    }
};

export const bulkCancelInvoiceItems = async (payload) => {
    const url = `${BASE_URL}/accounting/invoices/bulk_cancellation/`;
    try {
        const response = await axiosClient.delete(url, { data: payload });
        if (response.status === 202) {
            return { ...response.data, requiresCancellationOtp: true };
        }
        return response.data;
    } catch (error) {
        throw error;
    }
};

// ==============================|| PRESCRIPTION TEMPLATES API ||============================== //

/**
 * Fetch all prescription templates for the current doctor
 * Templates are stored in preferences settings as JSON string
 */
export const fetchPrescriptionTemplates = async () => {
    try {
        // Fetch preferences with app=false (user preferences)
        const response = await fetchUserPreferencesWithApp(false);

        // Templates are stored in settings as JSON string
        if (response?.settings) {
            let settingsObj;
            if (typeof response.settings === 'string') {
                try {
                    settingsObj = JSON.parse(response.settings);
                } catch (e) {
                    console.error('Error parsing settings JSON:', e);
                    return [];
                }
            } else {
                settingsObj = response.settings;
            }

            // Templates might be stored directly as array or in a templates key
            if (Array.isArray(settingsObj)) {
                return settingsObj;
            } else if (settingsObj.templates && Array.isArray(settingsObj.templates)) {
                return settingsObj.templates;
            }
        }

        return [];
    } catch (error) {
        console.error('Error fetching templates:', error);
        // Return empty array on error
        return [];
    }
};

/**
 * Fetch a single prescription template by ID
 * Templates are stored in preferences settings as JSON string
 */
export const fetchPrescriptionTemplate = async (templateId) => {
    try {
        const templates = await fetchPrescriptionTemplates();
        const template = templates.find(t => t.id === parseInt(templateId) || t.id === templateId);
        if (template) {
            return template;
        }
        throw new Error('Template not found');
    } catch (error) {
        console.error('Error fetching template:', error);
        throw error;
    }
};

/**
 * Create a new prescription template
 * Templates are stored in preferences settings as JSON string
 */
export const createPrescriptionTemplate = async (templateData) => {
    try {
        // Get current preferences
        const currentPrefs = await fetchUserPreferencesWithApp(false);

        // Parse current settings
        let settingsObj = {};
        if (currentPrefs?.settings) {
            if (typeof currentPrefs.settings === 'string') {
                try {
                    settingsObj = JSON.parse(currentPrefs.settings);
                } catch (e) {
                    console.error('Error parsing settings:', e);
                    settingsObj = {};
                }
            } else {
                settingsObj = currentPrefs.settings || {};
            }
        }

        // Get current templates array
        let templates = [];
        if (Array.isArray(settingsObj)) {
            templates = settingsObj;
        } else if (settingsObj.templates && Array.isArray(settingsObj.templates)) {
            templates = settingsObj.templates;
        }

        // Generate ID for new template
        const newId = templates.length > 0
            ? Math.max(...templates.map(t => t.id || 0)) + 1
            : 1;

        // Add new template
        const newTemplate = {
            id: newId,
            name: templateData.name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            template_data: templateData.template_data || {}
        };

        templates.push(newTemplate);

        // Update settings - if settings was an array, replace it; otherwise update templates key
        let updatedSettings;
        if (Array.isArray(settingsObj)) {
            updatedSettings = templates;
        } else {
            updatedSettings = {
                ...settingsObj,
                templates: templates
            };
        }

        // Save via PATCH (will replace everything in settings)
        // Send settings as object, not stringified JSON
        await updateUserPreferencesWithApp(false, {
            settings: updatedSettings
        });

        return newTemplate;
    } catch (error) {
        console.error('Error creating template:', error);
        throw error;
    }
};

/**
 * Update an existing prescription template
 * Templates are stored in preferences settings as JSON string
 */
export const updatePrescriptionTemplate = async (templateId, templateData) => {
    try {
        // Get current preferences
        const currentPrefs = await fetchUserPreferencesWithApp(false);

        // Parse current settings
        let settingsObj = {};
        if (currentPrefs?.settings) {
            if (typeof currentPrefs.settings === 'string') {
                try {
                    settingsObj = JSON.parse(currentPrefs.settings);
                } catch (e) {
                    console.error('Error parsing settings:', e);
                    settingsObj = {};
                }
            } else {
                settingsObj = currentPrefs.settings || {};
            }
        }

        // Get current templates array
        let templates = [];
        if (Array.isArray(settingsObj)) {
            templates = settingsObj;
        } else if (settingsObj.templates && Array.isArray(settingsObj.templates)) {
            templates = settingsObj.templates;
        }

        // Find and update template
        const templateIndex = templates.findIndex(t => t.id === parseInt(templateId) || t.id === templateId);
        if (templateIndex === -1) {
            throw new Error('Template not found');
        }

        // Update template
        templates[templateIndex] = {
            ...templates[templateIndex],
            name: templateData.name || templates[templateIndex].name,
            template_data: templateData.template_data || templates[templateIndex].template_data,
            updated_at: new Date().toISOString()
        };

        // Update settings
        let updatedSettings;
        if (Array.isArray(settingsObj)) {
            updatedSettings = templates;
        } else {
            updatedSettings = {
                ...settingsObj,
                templates: templates
            };
        }

        // Save via PATCH (will replace everything in settings)
        // Send settings as object, not stringified JSON
        await updateUserPreferencesWithApp(false, {
            settings: updatedSettings
        });

        return templates[templateIndex];
    } catch (error) {
        console.error('Error updating template:', error);
        throw error;
    }
};

/**
 * Delete a prescription template
 * Templates are stored in preferences settings as JSON string
 */
// ==============================|| RADIOLOGY TEMPLATES API ||============================== //

/**
 * Fetch all radiology templates for the current doctor
 * Templates are stored in preferences settings as JSON string under radiology_templates key
 */
export const fetchRadiologyTemplates = async () => {
    try {
        // Fetch preferences with app=false (user preferences)
        const response = await fetchUserPreferencesWithApp(false);

        // Templates are stored in settings as JSON string
        if (response?.settings) {
            let settingsObj;
            if (typeof response.settings === 'string') {
                try {
                    settingsObj = JSON.parse(response.settings);
                } catch (e) {
                    console.error('Error parsing settings JSON:', e);
                    return [];
                }
            } else {
                settingsObj = response.settings;
            }

            // Get radiology_templates array
            if (settingsObj.radiology_templates && Array.isArray(settingsObj.radiology_templates)) {
                return settingsObj.radiology_templates;
            }
        }

        return [];
    } catch (error) {
        console.error('Error fetching radiology templates:', error);
        return [];
    }
};

/**
 * Fetch a single radiology template by ID
 */
export const fetchRadiologyTemplate = async (templateId) => {
    try {
        const templates = await fetchRadiologyTemplates();
        return templates.find(t => t.id === parseInt(templateId) || t.id === templateId) || null;
    } catch (error) {
        console.error('Error fetching radiology template:', error);
        throw error;
    }
};

/**
 * Create a new radiology template
 */
export const createRadiologyTemplate = async (templateData) => {
    try {
        // Get current preferences
        const currentPrefs = await fetchUserPreferencesWithApp(false);

        // Parse current settings
        let settingsObj = {};
        if (currentPrefs?.settings) {
            if (typeof currentPrefs.settings === 'string') {
                try {
                    settingsObj = JSON.parse(currentPrefs.settings);
                } catch (e) {
                    console.error('Error parsing settings:', e);
                    settingsObj = {};
                }
            } else {
                settingsObj = currentPrefs.settings || {};
            }
        }

        // Get current radiology templates array
        let templates = settingsObj.radiology_templates || [];
        if (!Array.isArray(templates)) {
            templates = [];
        }

        // Generate ID for new template
        const newId = templates.length > 0
            ? Math.max(...templates.map(t => t.id || 0)) + 1
            : 1;

        // Add new template
        const newTemplate = {
            id: newId,
            name: templateData.name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            template_data: templateData.template_data || {}
        };

        templates.push(newTemplate);

        // Update settings with radiology_templates
        const updatedSettings = {
            ...settingsObj,
            radiology_templates: templates
        };

        // Save via PATCH
        await updateUserPreferencesWithApp(false, {
            settings: updatedSettings
        });

        return newTemplate;
    } catch (error) {
        console.error('Error creating radiology template:', error);
        throw error;
    }
};

/**
 * Update an existing radiology template
 */
export const updateRadiologyTemplate = async (templateId, templateData) => {
    try {
        // Get current preferences
        const currentPrefs = await fetchUserPreferencesWithApp(false);

        // Parse current settings
        let settingsObj = {};
        if (currentPrefs?.settings) {
            if (typeof currentPrefs.settings === 'string') {
                try {
                    settingsObj = JSON.parse(currentPrefs.settings);
                } catch (e) {
                    console.error('Error parsing settings:', e);
                    settingsObj = {};
                }
            } else {
                settingsObj = currentPrefs.settings || {};
            }
        }

        // Get current radiology templates array
        let templates = settingsObj.radiology_templates || [];
        if (!Array.isArray(templates)) {
            templates = [];
        }

        // Find and update template
        const templateIndex = templates.findIndex(t => t.id === parseInt(templateId) || t.id === templateId);
        if (templateIndex === -1) {
            throw new Error('Template not found');
        }

        // Update template
        templates[templateIndex] = {
            ...templates[templateIndex],
            name: templateData.name || templates[templateIndex].name,
            template_data: templateData.template_data || templates[templateIndex].template_data,
            updated_at: new Date().toISOString()
        };

        // Update settings
        const updatedSettings = {
            ...settingsObj,
            radiology_templates: templates
        };

        // Save via PATCH
        await updateUserPreferencesWithApp(false, {
            settings: updatedSettings
        });

        return templates[templateIndex];
    } catch (error) {
        console.error('Error updating radiology template:', error);
        throw error;
    }
};

/**
 * Delete a radiology template
 */
export const deleteRadiologyTemplate = async (templateId) => {
    try {
        // Get current preferences
        const currentPrefs = await fetchUserPreferencesWithApp(false);

        // Parse current settings
        let settingsObj = {};
        if (currentPrefs?.settings) {
            if (typeof currentPrefs.settings === 'string') {
                try {
                    settingsObj = JSON.parse(currentPrefs.settings);
                } catch (e) {
                    console.error('Error parsing settings:', e);
                    settingsObj = {};
                }
            } else {
                settingsObj = currentPrefs.settings || {};
            }
        }

        // Get current radiology templates array
        let templates = settingsObj.radiology_templates || [];
        if (!Array.isArray(templates)) {
            templates = [];
        }

        // Filter out the template to delete
        const filteredTemplates = templates.filter(t =>
            (t.id !== parseInt(templateId) && t.id !== templateId)
        );

        // Update settings
        const updatedSettings = {
            ...settingsObj,
            radiology_templates: filteredTemplates
        };

        // Save via PATCH
        await updateUserPreferencesWithApp(false, {
            settings: updatedSettings
        });

        return { success: true };
    } catch (error) {
        console.error('Error deleting radiology template:', error);
        throw error;
    }
};

export const deletePrescriptionTemplate = async (templateId) => {
    try {
        // Get current preferences
        const currentPrefs = await fetchUserPreferencesWithApp(false);

        // Parse current settings
        let settingsObj = {};
        if (currentPrefs?.settings) {
            if (typeof currentPrefs.settings === 'string') {
                try {
                    settingsObj = JSON.parse(currentPrefs.settings);
                } catch (e) {
                    console.error('Error parsing settings:', e);
                    settingsObj = {};
                }
            } else {
                settingsObj = currentPrefs.settings || {};
            }
        }

        // Get current templates array
        let templates = [];
        if (Array.isArray(settingsObj)) {
            templates = settingsObj;
        } else if (settingsObj.templates && Array.isArray(settingsObj.templates)) {
            templates = settingsObj.templates;
        }

        // Remove template
        templates = templates.filter(t => t.id !== parseInt(templateId) && t.id !== templateId);

        // Update settings
        let updatedSettings;
        if (Array.isArray(settingsObj)) {
            updatedSettings = templates;
        } else {
            updatedSettings = {
                ...settingsObj,
                templates: templates
            };
        }

        // Save via PATCH (will replace everything in settings)
        // Send settings as object, not stringified JSON
        await updateUserPreferencesWithApp(false, {
            settings: updatedSettings
        });

        return { success: true };
    } catch (error) {
        console.error('Error deleting template:', error);
        throw error;
    }
};

// ==============================|| PREFERENCES API ||============================== //

/**
 * Fetch user preferences with app parameter
 * @param {boolean} app - true for superuser preferences, false for user preferences
 */
export const fetchUserPreferencesWithApp = async (app = false) => {
    const url = `${BASE_URL}/users/preferences/user/`;
    try {
        const response = await axiosClient.get(url, {
            params: { app: app }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update user preferences with app parameter
 * @param {boolean} app - true for superuser preferences, false for user preferences
 * @param {object} preferencesData - Preferences data to update (will be stringified)
 */
export const updateUserPreferencesWithApp = async (app = false, preferencesData) => {
    const url = `${BASE_URL}/users/preferences/user/`;
    try {
        // Send as object - axios will handle JSON serialization
        const response = await axiosClient.patch(url, preferencesData, {
            params: { app: app }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch list of users for OTP recipients selection
 */
export const fetchUsersForOTP = async () => {
  const url = `${BASE_URL}/users/`;
  try {
    const response = await axiosClient.get(url, {
      params: { role: 'superuser' }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==============================|| PATIENT DOCUMENTS API ||============================== //

export const fetchPatientDocuments = async (patientId) => {
    const url = `${BASE_URL}/users/patient_documents/?patient_id=${patientId}`;
    try {
        const response = await axiosClient.get(url);
        return response.data?.results || response.data || [];
    } catch (error) {
        throw error;
    }
};

export const uploadPatientDocument = async (patientId, file, documentType, documentName, labReportId = null, appointmentId = null) => {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('document_type', documentType || 'other');
    formData.append('document_name', documentName || file.name);
        formData.append('patient_id', patientId);
    if (labReportId) formData.append('lab_report_id', labReportId);
    if (appointmentId) formData.append('appointment_id', appointmentId);

        const response = await axiosClient.post(`${BASE_URL}/users/patient_documents/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
};

export const updatePatientDocument = async (documentId, metadata) => {
    const response = await axiosClient.patch(`${BASE_URL}/users/patient_documents/${documentId}/`, metadata);
    return response.data;
};

/**
 * Fetches document bytes with auth. Use with blob URLs for img/iframe/download — raw URLs 401 in new tabs.
 * @param {string|number} documentUrlOrId - Full `document` URL from API or document id
 * @returns {{ blob: Blob, contentType: string }}
 */
export const fetchPatientDocumentBlob = async (documentUrlOrId) => {
  const url =
    typeof documentUrlOrId === 'string' && /^https?:\/\//i.test(documentUrlOrId)
      ? documentUrlOrId
      : `${BASE_URL}/users/patient_documents/${documentUrlOrId}/view/`;
  const response = await axiosClient.get(url, { responseType: 'blob' });
  const contentType = response.headers['content-type'] || 'application/octet-stream';
  return { blob: response.data, contentType };
};

export const deletePatientDocument = async (documentId) => {
  const response = await axiosClient.delete(`${BASE_URL}/users/patient_documents/${documentId}/`);
  return response.data;
};

// ==============================|| SEND REPORT API ||============================== //

export const sendReport = async ({ userId, method, reportId, file }) => {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('method', method);
    formData.append('report_id', reportId);
    formData.append('file', file, 'prescription.pdf');

    return axiosClient.post(`${BASE_URL}/lab/lab-report/send_report/`, formData);
};

// ==============================|| ABDM M3 CONSENT APIs ||============================== //

export const requestConsent = async (payload) => {
    const url = `${BASE_URL}/abha/api/v3/consent/request/`;
    try {
        const response = await axiosClient.post(url, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getConsentList = async (patientId) => {
    const url = patientId 
        ? `${BASE_URL}/abha/api/v3/consent/get_list/?patient=${patientId}` 
        : `${BASE_URL}/abha/api/v3/consent/get_list/`;
    try {
        const response = await axiosClient.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const viewFhirRecords = async (consentId) => {
    const url = `${BASE_URL}/abha/api/v3/consent/view_records/?consent_id=${consentId}`;
    try {
        // The consent screen shows its own message; no global error toast.
        const response = await axiosClient.get(url, { skipErrorSnackbar: true });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// ==============================|| ABDM M2 HIP LINKING APIs ||============================== //
//
// HIP (Health Information Provider) linking = "we are the hospital that generated
// this data; here is a link token so the patient can attach these care contexts to
// their ABHA locker via their PHR app." Complementary to the HIU consent flow in
// `AbhaConsentManager` (which is the "pull records from other providers" side).

/**
 * Step 1 of HIP linking: fetch a link token for the given ABHA address.
 * The patient will use this token (or the notification triggered by it) to
 * approve linking on their PHR app.
 *
 * @param {Object} payload
 * @param {string} payload.abha_address - Patient's ABHA address, e.g. `patient@sbx`
 * @param {number} payload.abha_number  - 14-digit ABHA number as integer (backend requires int)
 * @param {string} payload.full_name    - Patient full name
 * @param {'M'|'F'|'O'} payload.gender  - Normalised single-char gender
 * @param {number} payload.dob          - Year of birth as integer (backend requires int)
 */
export const getHipLinkToken = async (payload) => {
    const url = `${BASE_URL}/abha/api/v3/hip-link/get_link_token/`;
    try {
        const response = await axiosClient.post(url, payload);
        return { data: response.data, status: response.status };
    } catch (error) {
        throw error;
    }
};

/**
 * Step 2 of HIP linking: attach one or more care contexts (OP consultation,
 * discharge summary, diagnostic report, prescription, immunization record) to
 * the previously-linked ABHA address.
 *
 * @param {Object} payload
 * @param {string} payload.abhaAddress - Patient's ABHA address
 * @param {Array<Object>} payload.patient - Per ABDM spec:
 *   [{ referenceNumber, display, careContexts: [{ referenceNumber, display }], hiTypes, count }]
 */
export const linkCareContext = async (payload) => {
    const url = `${BASE_URL}/abha/api/v3/hip-link/link_care_context/`;
    try {
        const response = await axiosClient.post(url, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const generateAbhaUpdateMobileOtp = async (mobile_number, txn_id) => {
    const url = `${BASE_URL}/abha/api/v3/enrollment/generate_update_mobile_otp/`;
    try {
        const response = await axiosClient.post(url, { mobile_number, txn_id });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const verifyAbhaUpdateMobileOtp = async (otp, txn_id) => {
    const url = `${BASE_URL}/abha/api/v3/enrollment/auth_by_abdm/`;
    try {
        const response = await axiosClient.post(url, { otp, txn_id });
        return response.data;
    } catch (error) {
        throw error;
    }
};
